import type {
  FirstConsultation,
  OutcomeOption,
} from '@/api/firstConsultations/firstConsultationsApi.types';
import type { OwnFirstConsultationAnswer } from '@/api/ownFirstConsultation/ownFirstConsultationApi.types';
import { localizedLabel } from './localizedLabel';

export interface OwnerPreviewContent {
  answers: OwnFirstConsultationAnswer[];
  outcome: string | null;
  notes: string | null;
}

export interface OwnerPreviewBooleanLabels {
  yes: string;
  no: string;
}

function isFilled(value: string): boolean {
  return value.trim() !== '';
}

function resolveDisplayValue(
  fieldType: string,
  value: string,
  booleanLabels: OwnerPreviewBooleanLabels
): string {
  if (fieldType !== 'boolean') {
    return value;
  }
  if (value === 'true') {
    return booleanLabels.yes;
  }
  if (value === 'false') {
    return booleanLabels.no;
  }
  return value;
}

function resolveOutcomeLabel(
  outcomeCode: string | null,
  outcomeOptions: OutcomeOption[]
): string | null {
  if (outcomeCode === null) {
    return null;
  }
  const matchingOption = outcomeOptions.find(function matchesCode(option) {
    return option.code === outcomeCode;
  });
  if (matchingOption === undefined) {
    return null;
  }
  return localizedLabel(matchingOption.labelEn, matchingOption.labelPl);
}

export function buildOwnerPreviewContent(
  consultation: FirstConsultation,
  outcomeOptions: OutcomeOption[],
  booleanLabels: OwnerPreviewBooleanLabels
): OwnerPreviewContent {
  return {
    answers: consultation.answers
      .filter(function hasFilledValue(answer) {
        return isFilled(answer.value);
      })
      .map(function toOwnerAnswer(answer) {
        return {
          section: answer.section,
          label: localizedLabel(answer.labelEn, answer.labelPl),
          value: resolveDisplayValue(answer.fieldType, answer.value, booleanLabels),
        };
      }),
    outcome: resolveOutcomeLabel(consultation.outcomeCode, outcomeOptions),
    notes:
      consultation.therapistNotes !== null && isFilled(consultation.therapistNotes)
        ? consultation.therapistNotes
        : null,
  };
}
